import {useEffect, useState} from 'react';

import {Link, useSearchParams} from 'react-router-dom';

import {Button} from '@common-web/components/ui/button';
import {DataTable, useDataTable, type Column} from '@common-web/components/data-table';
import {ExportCsvButton} from '@common-web/components/export-csv-button';

import {FileText, Eye, Building, Calendar, Clock} from 'lucide-react';

import {PageHeader} from '@common-web/components/page-header';
import {ActionRequiredBanner} from '@common-web/components/action-required-banner';

import {
  getCompanyName,
  getRfcStatusDisplayName,
  RFC_WORKFLOW_STATUSES,
  RfcStatusBadge,
} from '@common-web/components/rfc/RfcStatus';

import {format, formatDistanceToNowStrict} from 'date-fns';

import {useIsInternal} from '@common-web/providers/portalProvider';
import {CbsQueueWidget} from '@common-web/components/rfc/CbsQueueWidget';
import {RouterOutput, trpc} from '@common-web/lib/trpc';
import {RfcStatus, getActionRequiredRfcStatuses, type RfcActorRole} from '@common/types/rfc';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useHasPermission} from '@common-web/hooks/use-permissions';

type SortKey = 'createdAt' | 'companyName' | 'status' | 'assignedBroker';
type RfcRow = RouterOutput['clients']['rfc']['listForAdmin']['data'][number];
type Filters = {status: string; client: string};

// Filter presets apply a fixed set of statuses that represent a workflow stage.
// `READY_FOR_CBS` = post-broker-approval, pre-group-creation. Matches the CBS
// handoff queue surfaced by CbsQueueWidget.
type FilterPreset = 'ALL' | 'READY_FOR_CBS';

const PRESET_STATUSES: Record<Exclude<FilterPreset, 'ALL'>, RfcStatus[]> = {
  READY_FOR_CBS: [RfcStatus.CLIENT_APPROVED, RfcStatus.MSA_SIGNED],
};

export const READY_FOR_CBS_STATUSES = PRESET_STATUSES.READY_FOR_CBS;

// Pseudo-status option in the status filter that expands into the
// actor-role-specific set of statuses requiring action.
const ACTION_REQUIRED = 'ACTION_REQUIRED';

export type RfcSubmissionsListProps = {
  getReviewHref: (rfcId: string) => string;
};

export function RfcSubmissionsList({getReviewHref}: RfcSubmissionsListProps) {
  const isInternal = useIsInternal();
  const isRfcAdmin = useHasPermission(CODE_PERMISSIONS.rfc.cbsQueue.key);
  const [searchParams, setSearchParams] = useSearchParams();

  const table = useDataTable<Filters>({
    defaultPageSize: 20,
    defaultFilters: {status: '', client: ''},
    searchDebounce: 900,
  });

  const [preset, setPreset] = useState<FilterPreset>(() =>
    searchParams.get('preset') === 'ready_for_cbs' && isRfcAdmin ? 'READY_FOR_CBS' : 'ALL',
  );

  const statusFilter = table.filterValues.status;
  const clientFilter = table.filterValues.client;

  // The Action Required banner lives on this page, so clicking it only updates
  // the query string (no remount). Translate the deep-link param into the
  // table's status filter (which useDataTable keeps in the URL itself), clear
  // any active preset, and drop the param so a later banner click re-triggers.
  useEffect(() => {
    if (searchParams.get('filter') === 'action-required') {
      table.setFilter('status', ACTION_REQUIRED);
      setPreset('ALL');
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('filter');
          next.delete('preset');
          return next;
        },
        {replace: true},
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Manually picking a status while a preset is active clears the preset —
  // last-selected filter wins, avoiding "two filters, neither visibly wins".
  useEffect(() => {
    if (preset !== 'ALL' && statusFilter) {
      setPreset('ALL');
      setSearchParams(
        (prev) => {
          if (!prev.has('preset')) return prev;
          const next = new URLSearchParams(prev);
          next.delete('preset');
          return next;
        },
        {replace: true},
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const clientsQuery = trpc.internal.client.list.useQuery(undefined, {
    enabled: isInternal,
    refetchOnWindowFocus: false,
  });

  const clientIdForQuery = isInternal && clientFilter ? clientFilter : undefined;

  const actorRole: RfcActorRole = isInternal ? 'internal' : 'client';
  const actionRequiredStatuses = getActionRequiredRfcStatuses(actorRole);

  // A preset (or the "Action Required" filter) overrides the single-status
  // dropdown and sends a `statuses` array to the API. When neither is active,
  // fall back to the single-status behavior.
  const statusesForQuery =
    preset !== 'ALL'
      ? PRESET_STATUSES[preset]
      : statusFilter === ACTION_REQUIRED
        ? actionRequiredStatuses
        : undefined;
  const statusForQuery =
    preset === 'ALL' && statusFilter && statusFilter !== ACTION_REQUIRED
      ? (statusFilter as RfcStatus)
      : undefined;

  const rfcsQuery = trpc.clients.rfc.listForAdmin.useQuery({
    page: table.page,
    limit: table.pageSize,
    search: table.debouncedSearch || undefined,
    sortKey: table.sort?.key as SortKey | undefined,
    sortDirection: table.sort?.direction ?? 'desc',
    status: statusForQuery,
    statuses: statusesForQuery,
    clientId: clientIdForQuery,
  });

  const exportCsvQuery = trpc.clients.rfc.exportCsv.useQuery(
    {
      search: table.debouncedSearch || undefined,
      status: statusForQuery,
      statuses: statusesForQuery,
      clientId: clientIdForQuery,
    },
    {enabled: false},
  );

  const applyPreset = (next: FilterPreset) => {
    setPreset(next);
    // Clear the single-status dropdown when a preset takes over (also resets page 1).
    if (next !== 'ALL' && statusFilter) {
      table.setFilter('status', '');
    } else {
      table.setPage(1);
    }
    // Keep URL in sync so the widget's link is shareable and the back button works.
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === 'READY_FOR_CBS') {
          nextParams.set('preset', 'ready_for_cbs');
        } else {
          nextParams.delete('preset');
        }
        return nextParams;
      },
      {replace: true},
    );
  };

  const pagination = rfcsQuery.data?.pagination;

  const hasActiveFilters =
    !!table.debouncedSearch || !!statusFilter || preset !== 'ALL' || (isInternal && !!clientFilter);

  const columns: Column<RfcRow>[] = [
    {
      id: 'createdAt',
      header: 'Date Submitted',
      sortable: true,
      render: (rfc) => (
        <div className='flex items-center space-x-2 text-sm text-brand-gray'>
          <Calendar className='h-3 w-3' />
          <span>
            {rfc.submittedAt
              ? format(new Date(rfc.submittedAt), 'M/d/yy')
              : rfc.createdAt
                ? format(new Date(rfc.createdAt), 'M/d/yy')
                : '—'}
          </span>
        </div>
      ),
    },
    {
      id: 'companyName',
      header: 'Company',
      sortable: true,
      render: (rfc) => (
        <div className='flex items-center space-x-3'>
          <Building className='h-4 w-4 text-brand-gray' />
          <p className='font-medium text-brand-navy'>{getCompanyName(rfc)}</p>
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      render: (rfc) => <RfcStatusBadge status={rfc.status as RfcStatus} />,
    },
    {
      id: 'assignedBroker',
      header: 'Assigned Broker',
      sortable: true,
      render: (rfc) => (
        <span className='text-sm text-brand-gray'>{rfc.assignedBroker?.name || '—'}</span>
      ),
    },
    ...(isInternal
      ? [
          {
            id: 'client',
            header: 'Client',
            render: (rfc) => <span className='text-sm text-brand-gray'>{rfc.client.name}</span>,
          } satisfies Column<RfcRow>,
          {
            id: 'rfcAge',
            header: 'RFC Age',
            sortable: true,
            sortKey: 'createdAt',
            render: (rfc) => (
              <div className='flex items-center space-x-2 text-sm text-brand-gray'>
                <Clock className='h-3 w-3' />
                <span>
                  {rfc.createdAt ? formatDistanceToNowStrict(new Date(rfc.createdAt)) : '—'}
                </span>
              </div>
            ),
          } satisfies Column<RfcRow>,
        ]
      : []),
    {
      id: 'actions',
      header: 'Actions',
      render: (rfc) => (
        <Button variant='outline' size='sm' asChild>
          <Link to={getReviewHref(rfc.id)}>
            <Eye className='h-4 w-4 mr-1' />
            View
          </Link>
        </Button>
      ),
    },
  ];

  const statusFilterOptions = [
    {value: ACTION_REQUIRED, label: 'Action Required'},
    ...RFC_WORKFLOW_STATUSES.map((status) => ({
      value: status as string,
      label: getRfcStatusDisplayName(status),
    })),
  ];

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='RFC Submissions'
          description='Review and manage all request for coverage submissions'
          icon={<FileText className='h-6 w-6' />}
          isFetching={rfcsQuery.isFetching}
          isLoading={rfcsQuery.isLoading}
        />

        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          <ActionRequiredBanner reviewHref='/rfc/submissions?filter=action-required' />
          {isRfcAdmin && <CbsQueueWidget />}
          <div className='flex flex-wrap items-center gap-2 mb-4'>
            {isRfcAdmin && (
              <>
                <span className='text-sm font-medium text-brand-gray mr-1'>Views:</span>
                <Button
                  variant={preset === 'ALL' ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => applyPreset('ALL')}
                >
                  All
                </Button>
                <Button
                  variant={preset === 'READY_FOR_CBS' ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => applyPreset('READY_FOR_CBS')}
                >
                  Ready for CBS
                </Button>
              </>
            )}
            <div className='ml-auto'>
              <ExportCsvButton refetch={exportCsvQuery.refetch} filenamePrefix='rfc-submissions' />
            </div>
          </div>

          <DataTable<RfcRow, Filters>
            table={table}
            columns={columns}
            data={rfcsQuery.data?.data}
            total={pagination?.total ?? 0}
            totalPages={pagination?.totalPages ?? 0}
            loading={rfcsQuery.isLoading}
            error={rfcsQuery.error}
            emptyMessage={
              hasActiveFilters
                ? 'No submissions found — try adjusting your search or filters'
                : 'No RFC submissions have been created yet'
            }
            title='Submissions'
            entity={{singular: 'submission', plural: 'submissions'}}
            searchPlaceholder='Search by company name...'
            filters={[
              {
                key: 'status',
                label: 'Statuses',
                type: 'select',
                placeholder: 'All Statuses',
                options: statusFilterOptions,
              },
              ...(isInternal
                ? [
                    {
                      key: 'client' as const,
                      label: 'Clients',
                      type: 'select' as const,
                      placeholder: 'All Clients',
                      options: (clientsQuery.data ?? []).map((client) => ({
                        value: client.id,
                        label: client.name,
                      })),
                    },
                  ]
                : []),
            ]}
            rowKey={(rfc) => rfc.id}
          />
        </div>
      </div>
    </div>
  );
}
